import "./ai-prompt.css";

import { useMutation } from "@apollo/client";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import DoDisturbIcon from "@mui/icons-material/DoDisturb";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SaveIcon from "@mui/icons-material/Save";
import { Button, Chip, IconButton, TextField } from "@mui/material";
import React, { ReactElement } from "react";

import {
  CreateBackgroundDocument,
  GenerateBackgroundDocument,
} from "../../generated/gql/graphql";
import { colorAiDisabled, theme } from "../../styles/mui-theme";
import { downloadFile } from "../../utils/download-files";

import { GeneratedBackgroundArea } from "./generated-background-area";

enum GenerateBackgroundStatus {
  Idle,
  Generating,
  Success,
  Error,
  Uploading,
}

export type GenerateAIBackgroundProps = {
  onBackgroundIdChange: (backgroundId: string) => void;
  onClose: () => void;
};

export function GenerateAIBackground({
  onBackgroundIdChange,
  onClose,
}: GenerateAIBackgroundProps): ReactElement {
  const [prompt, setPrompt] = React.useState("");
  const [
    generateBackground,
    {
      loading: generateBackgroundLoading,
      error: generateBackgroundError,
      data,
      reset: generateBackgroundReset,
    },
  ] = useMutation(GenerateBackgroundDocument, { errorPolicy: "all" });
  const [
    createBackground,
    {
      loading: createBackgroundLoading,
      error: createBackgroundError,
      reset: createBackgroundReset,
    },
  ] = useMutation(CreateBackgroundDocument, { errorPolicy: "all" });
  const [generatedBackgroundName, setGeneratedBackgroundName] =
    React.useState("");
  const [generatedBackgroundImage, setGeneratedBackgroundImage] =
    React.useState<string | undefined>(undefined);
  const [uploading, setUploading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState<Error | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = React.useState(0);

  React.useEffect(() => {
    if (!generateBackgroundLoading) {
      setElapsedSeconds(0);
      return;
    }
    const interval = setInterval(() => {
      setElapsedSeconds((s) => s + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [generateBackgroundLoading]);

  React.useEffect(() => {
    const imageBase64 = data?.generateBackground.data;
    if (!imageBase64) {
      return;
    }
    setGeneratedBackgroundImage(
      `data:${data?.generateBackground.mimeType};base64,${imageBase64}`,
    );
  }, [data?.generateBackground.data, data?.generateBackground.mimeType]);

  async function handleGenerate(): Promise<void> {
    generateBackground({
      variables: {
        input: {
          prompt,
        },
      },
    });
  }

  function handleDownload(): void {
    const imageBase64 = data?.generateBackground.data;
    const imageMimeType = data?.generateBackground.mimeType;
    if (!imageBase64) {
      return;
    }

    downloadFile(
      imageBase64,
      imageMimeType ?? "image/jpeg",
      "generated-background",
    );
  }

  function handleUse(): void {
    const imageBase64 = data?.generateBackground.data;
    const imageMimeType = data?.generateBackground.mimeType;
    if (!imageBase64) {
      return;
    }

    // Convert into File object to upload as background
    const timestamp = Date.now();
    const imageBytes = new Uint8Array(imageBase64.length);
    const imageBinary = atob(imageBase64);
    for (let i = 0; i < imageBinary.length; i++)
      imageBytes[i] = imageBinary.charCodeAt(i);
    const image = new File([imageBytes], `gen-${timestamp}.jpeg`, {
      type: imageMimeType ?? undefined,
    });
    setUploading(true);
    setUploadError(null);
    createBackground({
      variables: {
        input: {
          name: generatedBackgroundName,
          image,
        },
      },
    })
      .then((mutationResult) => {
        const createBackgroundResult = mutationResult.data?.createBackground;
        if (createBackgroundResult?.__typename === "CreateBackgroundSuccess") {
          // Use as background in the editor and close the panel
          onBackgroundIdChange(createBackgroundResult.background.id);
          onClose();
        } else if (
          createBackgroundResult?.__typename === "CreateBackgroundError"
        ) {
          // Show error to user
          setUploadError(new Error(createBackgroundResult.message));
        }
      })
      .catch((error) => {
        setUploadError(error as Error);
      })
      .finally(() => {
        setUploading(false);
      });
  }

  function handleEditPrompt(): void {
    // Show the prompt in an input to allow user to edit and generate again
    generateBackgroundReset();
    createBackgroundReset();
    setUploadError(null);
  }

  function handleReset(): void {
    setPrompt("");
    setGeneratedBackgroundImage(undefined);
    generateBackgroundReset();
    createBackgroundReset();
    setUploadError(null);
  }

  const error = generateBackgroundError || createBackgroundError || uploadError;
  const status = generateBackgroundLoading
    ? GenerateBackgroundStatus.Generating
    : error
      ? GenerateBackgroundStatus.Error
      : data
        ? GenerateBackgroundStatus.Success
        : createBackgroundLoading || uploading
          ? GenerateBackgroundStatus.Uploading
          : GenerateBackgroundStatus.Idle;

  let statusIndicator;
  let statusIndicatorColor;
  let statusIndicatorTextColor;
  let generatedBackground: string | undefined = generatedBackgroundImage;
  let content;
  let actions;

  if (status === GenerateBackgroundStatus.Idle) {
    statusIndicator = "Waiting for prompt...";
    content = (
      <TextField
        className="w-full ai-prompt"
        color="ai"
        style={{
          borderColor: theme.palette.ai.main,
          color: theme.palette.ai.dark,
        }}
        rows={3}
        placeholder="What do you want to create?"
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        multiline
      />
    );
    actions = (
      <div className="mt-3 flex justify-end">
        <Button
          variant="contained"
          color="ai"
          disabled={!prompt}
          endIcon={<AutoAwesomeIcon />}
          onClick={() => handleGenerate()}
        >
          Create
        </Button>
      </div>
    );
  }

  if (status === GenerateBackgroundStatus.Generating) {
    statusIndicator = `Generating Background (${elapsedSeconds} sec)`;
    statusIndicatorColor = colorAiDisabled;
    statusIndicatorTextColor = theme.palette.ai.dark;
    actions = (
      <div className="flex justify-end">
        <Button
          variant="contained"
          color="ai"
          endIcon={<DoDisturbIcon />}
          onClick={handleEditPrompt}
        >
          Stop
        </Button>
      </div>
    );
  }

  if (status === GenerateBackgroundStatus.Error) {
    statusIndicator = `Error generating background: ${error?.message}`;
    statusIndicatorColor = "#F9BAAF";
    statusIndicatorTextColor = "#9F050C";
    actions = (
      <div className="flex justify-end">
        <Button
          variant="contained"
          color="ai"
          endIcon={<RestartAltIcon />}
          onClick={handleEditPrompt}
        >
          Try Again
        </Button>
      </div>
    );
  }
  if (status === GenerateBackgroundStatus.Success) {
    statusIndicator = "Background Generation successful";
    statusIndicatorColor = "#D2F2D2";
    statusIndicatorTextColor = "#2D5237";
    actions = (
      <div className="flex justify-between">
        <Button endIcon={<DeleteIcon />} onClick={handleReset}>
          Discard
        </Button>
        <div>
          <Button
            className="ms-3"
            color="info"
            variant="contained"
            endIcon={<EditIcon />}
            onClick={handleEditPrompt}
          >
            Edit Prompt
          </Button>
          <Button
            className="ms-3"
            color="info"
            variant="contained"
            endIcon={<SaveIcon />}
            onClick={handleUse}
            disabled={
              !generatedBackgroundName || createBackgroundLoading || uploading
            }
          >
            Save
          </Button>
          <IconButton
            className="ms-3"
            size="small"
            color="primary"
            onClick={handleDownload}
            aria-label="Download generated background"
          >
            <DownloadIcon />
          </IconButton>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col">
      <div className="flex justify-between py-3 px-4">
        <Chip
          className="h-full font-bold rounded-md"
          label={statusIndicator}
          sx={{
            backgroundColor: statusIndicatorColor,
          }}
          style={{
            color: statusIndicatorTextColor,
          }}
        />
        <Button onClick={onClose} endIcon={<CloseIcon />}>
          Cancel
        </Button>
      </div>
      <GeneratedBackgroundArea
        generatedBackground={generatedBackground}
        generatedBackgroundName={generatedBackgroundName}
        isGenerating={status === GenerateBackgroundStatus.Generating}
        onGeneratedBackgroundNameChange={setGeneratedBackgroundName}
      />
      <div
        className="flex flex-col p-4"
        style={{
          backgroundColor: "#F9EFFF",
          borderTop: `1px solid ${theme.palette.ai.main}`,
        }}
      >
        <div>{content}</div>
        <div className="w-full">{actions}</div>
      </div>
    </div>
  );
}
